import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Landmark, Disc3, Music, Mic, Camera, Video, PartyPopper, UtensilsCrossed, ChefHat,
  Speaker, Lightbulb, Theater, MonitorPlay, Drum, Palette, Flower2, Shirt, Scissors,
  Brush, Cake, CarFront, BedDouble, Star, Sparkles, Baby, Heart, MoreHorizontal,
  CalendarCheck, SearchCheck, MessagesSquare, BadgeCheck, MapPin,
} from "lucide-react";
import api, { useTitle } from "../lib/api";
import SearchWidget from "../components/SearchWidget";
import AdSlot from "../components/AdSlot";

export const ICONS = {
  Landmark, Disc3, Music, Mic, Camera, Video, PartyPopper, UtensilsCrossed, ChefHat,
  Speaker, Lightbulb, Theater, MonitorPlay, Drum, Palette, Flower2, Shirt, Scissors,
  Brush, Cake, CarFront, BedDouble, Star, Sparkles, Baby, Heart, MoreHorizontal,
};

export function CategoryIcon({ name, size = 22, className = "" }) {
  const Icon = ICONS[name] || Sparkles;
  return <Icon size={size} className={className} />;
}

const PALETTE = ["#D4AF37", "#10B981", "#2563EB", "#EC4899", "#F59E0B", "#8B5CF6", "#14B8A6", "#EF4444", "#F97316", "#06B6D4"];

export function CategoryGrid({ categories }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {categories.map((c, i) => {
        const col = PALETTE[i % PALETTE.length];
        return (
          <Link key={c.id} to={`/ara?category=${c.id}`} data-testid={`category-card-${c.slug}`}
            className="card p-5 flex flex-col items-center gap-3 text-center hover:scale-[1.03] hover:shadow-2xl transition-all duration-300 group"
            style={{ borderColor: `${col}44` }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
              style={{ color: col, border: `1px solid ${col}66`, backgroundColor: `${col}1f`, boxShadow: `0 0 24px ${col}22` }}>
              <CategoryIcon name={c.icon} />
            </div>
            <div>
              <div className="font-bold text-sm text-slate-100">{c.name}</div>
              <div className="text-xs mt-0.5" style={{ color: `${col}bb` }}>{c.company_count ?? ""} {c.company_count != null ? "firma" : ""}</div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export default function HomePage() {
  useTitle("Etkinliğiniz için ihtiyacınız olan her şey tek yerde");
  const [data, setData] = useState({ categories: [], featured: [] });
  const [marqueeAds, setMarqueeAds] = useState([]);

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
    api.get("/ads?placement=hero_marquee").then((r) => setMarqueeAds(r.data)).catch(() => {});
  }, []);

  const marqueeItems = [...data.categories.map((c) => c.name), ...marqueeAds.map((a) => a.name)];

  return (
    <div>
      <section className="relative overflow-hidden">
        <img src="https://images.pexels.com/photos/4717550/pexels-photo-4717550.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="Düğün salonu" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070C1E]/70 via-[#070C1E]/85 to-[#070C1E]" />
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2563EB]/20 blur-3xl pointer-events-none" />
        <div className="absolute top-24 -right-24 w-80 h-80 rounded-full bg-[#EC4899]/15 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-[#10B981]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-14">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="badge-gold mb-5">Türkiye'nin etkinlik hizmetleri pazaryeri</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white max-w-3xl">
              Etkinliğiniz için ihtiyacınız olan{" "}
              <span className="text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>her şey</span>{" "}
              tek yerde.
            </h1>
            <p className="text-base sm:text-lg text-slate-400 font-medium mt-4 max-w-xl">
              Tarihinizi ve konumunuzu seçin, müsait hizmetleri keşfedin.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="mt-8">
            <SearchWidget />
          </motion.div>
          <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-400">
            <span className="flex items-center gap-2"><BadgeCheck size={16} className="text-[#D4AF37]" /> Onaylı firmalar</span>
            <span className="flex items-center gap-2"><CalendarCheck size={16} className="text-[#10B981]" /> Gerçek zamanlı müsaitlik</span>
            <span className="flex items-center gap-2"><MapPin size={16} className="text-[#D4AF37]" /> 81 il, 973 ilçe</span>
          </div>
        </div>
      </section>

      <div className="relative overflow-hidden border-y border-white/10 bg-[#0B132B]/70 py-3.5" data-testid="hero-marquee">
        <div className="marquee-track flex items-center gap-10 whitespace-nowrap w-max">
          {marqueeItems.concat(marqueeItems).map((t, i) => (
            <span key={i} className="flex items-center gap-10 text-sm font-semibold text-slate-300">
              {t}
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <AdSlot placement="home_top" className="mt-6" />

        <section className="mt-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Popüler Kategoriler</h2>
              <p className="text-sm text-slate-400 mt-1">Düğün salonundan DJ'e, fotoğrafçıdan catering'e kadar her şey.</p>
            </div>
            <Link to="/kategoriler" className="text-sm font-semibold text-[#D4AF37] hover:underline shrink-0">Tümü →</Link>
          </div>
          <CategoryGrid categories={data.categories.slice(0, 10)} />
          <AdSlot placement="home_category" className="mt-6" />
        </section>

        <section className="mt-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6">Öne Çıkan Firmalar</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.featured.map((c) => (
              <Link key={c.id} to={`/firma/${c.slug}`} data-testid={`featured-company-${c.slug}`}
                className="card overflow-hidden hover:scale-[1.02] hover:border-[#D4AF37]/40 hover:shadow-2xl transition-all duration-300">
                <div className="h-36 relative">
                  <img src={c.logo} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent" />
                </div>
                <div className="p-4">
                  <div className="font-bold text-slate-100 flex items-center gap-1.5">{c.name} <BadgeCheck size={15} className="text-[#D4AF37]" /></div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={12} /> {c.city} / {c.district}</div>
                  <div className="text-xs text-[#D4AF37] font-semibold mt-2">{c.resource_count} kaynak</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section id="nasil-calisir" className="mt-20 scroll-mt-24">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white text-center mb-10">Nasıl Çalışır?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: CalendarCheck, t: "Tarih & Konum Seçin", d: "Etkinlik tarihinizi, il ve ilçenizi seçin. Misafir sayınızı belirtin.", c: "#D4AF37" },
              { icon: SearchCheck, t: "Müsaitleri Görün", d: "O tarihte müsait salonları, ekipleri ve kaynakları anında karşılaştırın.", c: "#10B981" },
              { icon: MessagesSquare, t: "İletişime Geçin", d: "WhatsApp, telefon veya rezervasyon isteği ile firmaya doğrudan ulaşın.", c: "#2563EB" },
            ].map((s, i) => (
              <div key={i} className="card p-7 text-center" style={{ borderColor: `${s.c}33` }}>
                <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
                  style={{ color: s.c, backgroundColor: `${s.c}1a`, border: `1px solid ${s.c}44` }}>
                  <s.icon size={26} />
                </div>
                <div className="font-bold text-white mb-2">{i + 1}. {s.t}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        <AdSlot placement="mobile_banner" className="mt-16 md:hidden" />

        <section className="mt-20 card p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Firmanızı binlerce müşteriyle buluşturun</h2>
          <p className="text-slate-400 mt-3 max-w-lg mx-auto">Kaynaklarınızı ekleyin, takviminizi yönetin, müsait olduğunuz tarihlerde otomatik listelenin.</p>
          <Link to="/kayit?tip=firma" data-testid="cta-company-register" className="btn-gold mt-6 inline-flex">Ücretsiz Firma Kaydı</Link>
        </section>
      </div>
    </div>
  );
}

export function CategoriesPage() {
  useTitle("Kategoriler");
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/home").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-3xl font-bold text-white mb-2">Tüm Kategoriler</h1>
      <p className="text-slate-400 mb-8">İhtiyacınıza uygun hizmet kategorisini seçin.</p>
      <CategoryGrid categories={categories} />
    </div>
  );
}
